/* TanStack Query hooks over the typed API client. Registers use server-side
   pagination with keepPreviousData (no "0 rows" flash on page/sort change). */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { fetchFacets, fetchItem, fetchList, fetchMeta, fetchSub } from './api'
import type { ListQuery } from './contracts'

export const qk = {
  list: (resource: string, query: ListQuery) => [resource, 'list', query] as const,
  item: (resource: string, id: string) => [resource, 'item', id] as const,
  facets: (resource: string, by: readonly string[]) => [resource, 'facets', by.join(',')] as const,
  meta: (name: string) => ['meta', name] as const,
  sub: (path: string) => ['sub', path] as const,
  /* Префикс ресурса: ключи выше начинаются с него, поэтому одной инвалидацией
     сбрасываются и списки, и записи. Понадобится командам записи (Ф2.0). */
  all: (resource: string) => [resource] as const,
}

/* Очередь «Мои действия» и число на пункте навигации — одно состояние,
   разложенное на два запроса. Сбрасываются только парой: команда, сбросившая
   один список, оставляла бейдж прежним, и закрытая задача продолжала висеть
   числом 23 при пустой очереди. */
export const inboxKeys = [qk.sub('/inbox'), qk.sub('/nav-counters')] as const

/* signal прокидывается во все запросы: при быстрой смене фильтра или наборе в
   поиске устаревший запрос отменяется, а не догоняет ответом поверх свежего. */
export function useList<T>(resource: string, query: ListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.list(resource, query),
    queryFn: ({ signal }) => fetchList<T>(resource, query, { signal }),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  })
}

export function useFacets(resource: string, by: readonly string[]) {
  return useQuery({
    queryKey: qk.facets(resource, by),
    queryFn: ({ signal }) => fetchFacets(resource, by, { signal }),
    staleTime: 60_000,
    /* Пустой список полей — не запрос: сервер на «by=» отвечает 400, и в
       консоли висела ошибка там, где спрашивать было нечего (у проекта нет
       ни одного своего атрибута). */
    enabled: by.length > 0,
  })
}

export function useItem<T>(resource: string, id: string | null) {
  return useQuery({
    queryKey: qk.item(resource, id ?? ''),
    queryFn: ({ signal }) => fetchItem<T>(resource, id as string, { signal }),
    enabled: id != null && id !== '',
  })
}

/* Метаданные проекта: справочники, проектные атрибуты, кодирование.
 *
 * Раньше стояло staleTime: Infinity — «за сессию не меняются». Это неправда в
 * системе с несколькими администраторами: атрибут «TEST» заводит один, а у
 * второго ни колонка в реестре, ни колонка в выгружаемом шаблоне не появлялись
 * до перезагрузки страницы, и выглядело это как «шаблон не подтянул модель».
 *
 * Пять минут и перечитывание при возврате в окно: набор всё ещё не читается на
 * каждый рендер, но правка администратора доходит сама. Своя команда правки
 * по-прежнему сбрасывает ключ немедленно — ждать эти пять минут не нужно. */
export function useMeta<T>(name: string) {
  return useQuery({
    queryKey: qk.meta(name),
    queryFn: ({ signal }) => fetchMeta<T>(name, { signal }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useSub<T>(path: string | null) {
  return useQuery({
    queryKey: qk.sub(path ?? ''),
    queryFn: ({ signal }) => fetchSub<T>(path as string, { signal }),
    enabled: path != null && path !== '',
  })
}
